// src/utils/leaveDateUtils.js
//
// Leave business-date formatting helpers
// ======================================
//
// WHY THIS FILE EXISTS
// --------------------
// The backend stores leave boundaries as real UTC instants. For full-day leave
// requests these instants represent Europe/Warsaw local-midnight boundaries.
// Example during summer time:
//
//   selected business date: 2026-05-20
//   stored start_at:        2026-05-19T22:00:00.000Z
//
// The stored UTC instant is correct. What must be avoided is displaying the raw
// UTC calendar date to users. HR screens should display the business date/range.
//
// This helper centralizes that rule for Dashboard, HR tables, work items and
// cancellation selectors.

export const LEAVE_BUSINESS_TIME_ZONE = "Europe/Warsaw";

function toDateOrNull(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

function getDateTimePartsInTimeZone(value, timeZone = LEAVE_BUSINESS_TIME_ZONE) {
  const d = toDateOrNull(value);
  if (!d) return null;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);

  const byType = {};
  parts.forEach((part) => {
    if (part.type !== "literal") byType[part.type] = part.value;
  });

  if (!byType.year || !byType.month || !byType.day) return null;

  return {
    year: byType.year,
    month: byType.month,
    day: byType.day,
    hour: byType.hour || "00",
    minute: byType.minute || "00",
    second: byType.second || "00",
  };
}

export function dateOnlyInLeaveBusinessTimeZone(value) {
  const p = getDateTimePartsInTimeZone(value, LEAVE_BUSINESS_TIME_ZONE);
  return p ? `${p.year}-${p.month}-${p.day}` : null;
}

function isMidnightInLeaveBusinessTimeZone(value) {
  const p = getDateTimePartsInTimeZone(value, LEAVE_BUSINESS_TIME_ZONE);
  if (!p) return false;
  return p.hour === "00" && p.minute === "00" && p.second === "00";
}

function addDaysToDateOnly(dateOnly, days) {
  const s = String(dateOnly || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;

  const [y, m, d] = s.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + Number(days || 0));

  const yy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utc.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function formatDateTimeInLeaveBusinessTimeZone(value, locale) {
  const d = toDateOrNull(value);
  if (!d) return value ? String(value) : "—";

  return d.toLocaleString(locale, {
    timeZone: LEAVE_BUSINESS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getLeaveBusinessDateRange(row) {
  if (!row) {
    return {
      startDate: null,
      endDate: null,
      endExclusiveDate: null,
      rangeLabel: "—",
      fullDayRange: false,
    };
  }

  // Prefer backend-decorated fields when available. They are calculated by the
  // same business rule and are useful even if a future client runs outside the
  // salon's local time zone.
  const backendStart = row.business_start_date || row.meta?.business_start_date || null;
  const backendEnd = row.business_end_date || row.meta?.business_end_date || null;
  const backendEndExclusive = row.business_end_exclusive_date || row.meta?.business_end_exclusive_date || null;
  const backendRange = row.business_date_range || row.meta?.business_date_range || null;

  if (backendStart || backendEnd || backendRange) {
    return {
      startDate: backendStart,
      endDate: backendEnd || backendStart,
      endExclusiveDate: backendEndExclusive,
      rangeLabel: backendRange || (backendStart && backendEnd && backendStart !== backendEnd ? `${backendStart} → ${backendEnd}` : backendStart || backendEnd || "—"),
      fullDayRange: Boolean(row.business_full_day_range ?? row.meta?.business_full_day_range ?? true),
    };
  }

  const start = row.start_at || row.startAt || row.meta?.start_at;
  const end = row.end_at || row.endAt || row.meta?.end_at;
  const startDate = dateOnlyInLeaveBusinessTimeZone(start);
  const endDateInTz = dateOnlyInLeaveBusinessTimeZone(end);
  const fullDayRange = isMidnightInLeaveBusinessTimeZone(start) && isMidnightInLeaveBusinessTimeZone(end);

  let inclusiveEndDate = endDateInTz;
  if (fullDayRange && endDateInTz) {
    inclusiveEndDate = addDaysToDateOnly(endDateInTz, -1);
  }

  if (startDate && inclusiveEndDate && inclusiveEndDate < startDate) {
    inclusiveEndDate = startDate;
  }

  const rangeLabel = startDate && inclusiveEndDate
    ? startDate === inclusiveEndDate
      ? startDate
      : `${startDate} → ${inclusiveEndDate}`
    : "—";

  return {
    startDate,
    endDate: inclusiveEndDate,
    endExclusiveDate: endDateInTz,
    rangeLabel,
    fullDayRange,
  };
}

export function formatLeaveBusinessDateRange(row) {
  return getLeaveBusinessDateRange(row).rangeLabel;
}

export function formatLeaveBusinessStart(row, locale) {
  const range = getLeaveBusinessDateRange(row);
  if (range.fullDayRange) return range.startDate || "—";
  return formatDateTimeInLeaveBusinessTimeZone(row?.start_at || row?.startAt || row?.meta?.start_at, locale);
}

export function formatLeaveBusinessEnd(row, locale) {
  const range = getLeaveBusinessDateRange(row);
  if (range.fullDayRange) return range.endDate || "—";
  return formatDateTimeInLeaveBusinessTimeZone(row?.end_at || row?.endAt || row?.meta?.end_at, locale);
}

export default {
  dateOnlyInLeaveBusinessTimeZone,
  formatLeaveBusinessDateRange,
  formatLeaveBusinessEnd,
  formatLeaveBusinessStart,
  getLeaveBusinessDateRange,
};
